import React from "react";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { reinstateTruck, retireTruck } from "../lib/dataManagerApi.js";
import { isDompeurTruck, truckUnitKind } from "../utils/dataManagerFleetUnits.js";
import { retirementBullets, retirementHistoryName, retirementLabel, retirementPart, retirementUnitNames } from "../utils/dataManagerFleetRetirement.js";

const SECONDARY_BUTTON_CLASS_NAME = "inline-flex h-8 items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 text-[12px] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

// « Retirer de la flotte » ou « Remettre dans la flotte » : sur une seule ligne sélectionnée de l'onglet.
export function DataManagerFleetRetirementButton({ retired = false, truck = null, disabled = false, onOpen }) {
  return <button type="button" data-fleet-retirement-action={retired ? "reinstate" : "retire"} disabled={disabled || !truck}
    title={truck ? "" : "Sélectionnez une seule ligne."} onClick={() => onOpen?.(truck)} className={SECONDARY_BUTTON_CLASS_NAME}>
    {retired ? "Remettre dans la flotte" : "Retirer de la flotte"}
  </button>;
}

export function DataManagerFleetRetirementDialog({ truck, tab = "vehicles", mode = "retire", isBusy = false, onCancel, onConfirm }) {
  const reinstate = mode === "reinstate";
  const basePart = retirementPart(truck, tab);
  const [wholeLine, setWholeLine] = React.useState(false);
  // Date choisie par l'utilisateur ; sans elle, le serveur propose le prochain jour ouvrable.
  const [chosenDate, setChosenDate] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const part = wholeLine ? "ligne" : basePart;
  const { tractor, trailer } = retirementUnitNames(truck);
  // Toute la ligne : seulement si l'autre partie est dans le même état (encore dans la flotte au retrait, retirée à la remise).
  const otherPartRetired = Boolean(basePart === "remorque" ? truck?.tractor_retired_on : truck?.trailer_retired_on);
  const canChooseWholeLine = truckUnitKind(truck) === "ensemble" && !isDompeurTruck(truck) && otherPartRetired === reinstate;
  // L'aperçu vient du serveur : la même route, sans confirmation, répond par les conséquences à confirmer.
  React.useEffect(() => {
    let current = true;
    setErrorMessage("");
    const payload = reinstate ? { part, expected_updated_at: truck?.updated_at }
      : { part, expected_updated_at: truck?.updated_at, ...(chosenDate ? { effective_from: chosenDate } : {}) };
    (reinstate ? reinstateTruck : retireTruck)(truck.truck_id, payload).then(({ preview: nextPreview, result }) => {
      if (!current) return;
      if (result) {
        setErrorMessage("Cette ligne a changé. Fermez la fenêtre et recommencez.");
        return;
      }
      setPreview(nextPreview);
    }).catch((failure) => { if (current) { setPreview(null); setErrorMessage(failure.message); } });
    return () => { current = false; };
  }, [chosenDate, part, reinstate, truck]);
  const label = retirementLabel(truck, part);
  const feminine = part === "remorque";
  const hasHistory = Boolean(truck?.has_historical_assignments);
  const today = new Date().toLocaleDateString("en-CA");
  return <ConfirmationModal
    eyebrow={reinstate ? "Remise dans la flotte" : "Retrait de la flotte"}
    title={reinstate ? `Remettre ${label} dans la flotte ?` : `Retirer ${label} de la flotte ?`}
    tone="neutral"
    description={reinstate
      ? "La date de retrait est effacée. Rien n'est rattaché automatiquement et le chauffeur par défaut n'est pas rétabli."
      : `${hasHistory ? `${label} a roulé : ${feminine ? "elle ne peut pas être supprimée" : "il ne peut pas être supprimé"}. ` : ""}`
        + `${feminine ? "Retirée, elle" : "Retiré, il"} quitte la flotte à partir de la date choisie et garde tout son historique. `
        + `Vous pourrez ${feminine ? "la" : "le"} remettre.`}
    warningMessage={!reinstate && preview
      ? `Inchangé : les tournées jusqu'au ${preview.unchanged_until_label}, les relevés de Consolidation et la facturation gardent « ${retirementHistoryName(truck)} ».`
      : ""}
    errorMessage={errorMessage}
    confirmLabel={reinstate ? "Remettre dans la flotte" : "Retirer de la flotte"}
    confirmDisabled={!preview || Boolean(errorMessage)}
    isBusy={isBusy}
    onCancel={onCancel}
    onClose={onCancel}
    onDismiss={onCancel}
    onConfirm={() => onConfirm?.(reinstate
      ? { part, confirmed: true, expected_updated_at: truck?.updated_at }
      : { part, effective_from: preview.effective_from, confirmed_versions: preview.versions || {}, expected_updated_at: truck?.updated_at })}
  >
    <div className="mt-2 grid gap-2" data-fleet-retirement-dialog={mode}>
      {!reinstate ? <label className="flex flex-wrap items-center gap-2 rounded-[4px] border border-line-soft bg-white px-3 py-2 text-[0.84rem] text-text">
        <span className="font-semibold">{feminine ? "Retirée à partir du :" : "Retiré à partir du :"}</span>
        <input type="date" min={today} value={chosenDate || preview?.effective_from || ""} data-fleet-retirement-date
          onChange={(event) => { if (event.target.value) setChosenDate(event.target.value); }}
          className="h-8 rounded-[4px] border border-line-soft bg-surface px-2 text-[0.84rem]" />
      </label> : null}
      {canChooseWholeLine ? <label className="flex items-center gap-2 text-[0.82rem] text-muted-strong">
        <input type="checkbox" checked={wholeLine} onChange={(event) => setWholeLine(event.target.checked)} data-fleet-retirement-whole-line />
        {reinstate ? `Remettre aussi ${basePart === "remorque" ? `le camion ${tractor}` : `la remorque ${trailer}`}`
          : `Retirer aussi ${basePart === "remorque" ? `le camion ${tractor}` : `la remorque ${trailer}`} (toute la ligne)`}
      </label> : null}
      {preview ? <ul className="m-0 grid list-none gap-1.5 p-0" aria-label="Conséquences de l’action">
        {(reinstate ? [preview.message] : retirementBullets(truck, preview)).map((item) => <li key={item}
          className="flex items-start gap-2 text-[0.82rem] leading-5 text-muted-strong">
          <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-slate-500" />
          <span className="min-w-0 break-words text-pretty">{item}</span>
        </li>)}
      </ul> : !errorMessage ? <p className="m-0 text-[0.82rem] text-muted-strong">Lecture des conséquences…</p> : null}
    </div>
  </ConfirmationModal>;
}
